import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAeonasUnread, ChatBadge } from './AeonasUnread';
import { useAuth } from '../auth/AuthContext';
import './AeonasChat.css';
import ChatPushSettings from './ChatPushSettings';
import useChatPresence from './useChatPresence';
import useChatKeyboardViewport from './useChatKeyboardViewport';
import { FiTrash2, FiPlus } from 'react-icons/fi';
import './AeonasChatDelete.css';

const locationNames = { 1: 'Essen', 2: 'Münster', 3: 'Dortmund' };
function Avatar({ artistId, name, small = false }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [artistId]);
  return <span className={'ae-chat-avatar' + (small ? ' small' : '')}>
    {artistId && !broken && <img src={'/api/chat/avatars/' + encodeURIComponent(artistId)} alt="" loading="lazy" onError={() => setBroken(true)} />}
    <span>{(name || '?').trim().slice(0, 1).toUpperCase()}</span>
  </span>;
}

export default function AeonasChat({ selectedLocationId, setSelectedLocationId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const unread = useAeonasUnread();
  const lastMarked = useRef('');
  const messageGeneration = useRef(0);
  const [showContacts, setShowContacts] = useState(false);
  const management = ['owner', 'manager'].includes(user?.role);
  const ownLocation = Number(user?.locationId);
  const navigationTarget = useRef(null);
  const currentLocation = user?.role === 'artist' ? ownLocation : Number(selectedLocationId || ownLocation || 1);
  const [allStudios, setAllStudios] = useState(false);
  const [room, setRoom] = useState('dm');
  const [peer, setPeer] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const shell = useRef(null);
  const end = useRef(null);
  const prevLocation = useRef(currentLocation);
  const channel = 'dm';
  const countFor = key => Number(unread.counts?.[key]) || 0;
  const locationId = channel === 'studio' ? currentLocation : null;

  // Deep-link unread messages to the correct studio or private conversation.
  useEffect(() => {
    const query = new URLSearchParams(routeLocation.search);
    const nextChannel = query.get('channel');
    const nextLocation = Number(query.get('locationId'));
    const nextPeer = Number(query.get('peer'));
    if (nextChannel === 'dm' && Number.isSafeInteger(nextPeer) && nextPeer > 0) {
      if ([1,2,3].includes(nextLocation) && management) {
        if (management && prevLocation.current !== nextLocation && setSelectedLocationId) {
        navigationTarget.current = nextLocation;
        setSelectedLocationId(nextLocation);
      }
        setAllStudios(false);
      } else {
        setAllStudios(true);
      }
      setRoom('dm');
      setPeer(nextPeer);
    setShowContacts(false);
      setMobileOpen(true);
    } else if (nextChannel) {
      setPeer(null); setMobileOpen(false);
      navigate('/chat', { replace:true });
    }
  }, [routeLocation.search, management, ownLocation, setSelectedLocationId, navigate]);


  // Only the navbar selects the studio; explicit notification links sync it once.
  useEffect(() => {
    const selected = Number(selectedLocationId || ownLocation || 1);
    if (prevLocation.current !== selected) {
      prevLocation.current = selected;
      if (navigationTarget.current === selected) { navigationTarget.current = null; return; }
      setAllStudios(false);
      setRoom('dm');
      setPeer(null);
      setMobileOpen(false);
      setDraft('');
    }
  }, [selectedLocationId, ownLocation]);

  const loadPeople = useCallback(() => {
    axios.get('/api/chat/people').then(r => setPeople(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => {
    loadPeople();
    const interval = setInterval(loadPeople, 30000);
    return () => clearInterval(interval);
  }, [loadPeople]);

  const ownerContacts = useMemo(() => people.filter(p => p.role === 'owner'), [people]);
  const contacts = useMemo(() => {
    const filtered = people.filter(p => {
      // Owners are displayed separately as a permanent private contact.
      if (p.role === 'owner') return false;
      if (!allStudios) return Number(p.locationId) === currentLocation;
      return management || p.role === 'artist' || Number(p.locationId) === ownLocation;
    });
    // Distinct user IDs; never deduplicate different logins by name.
    return filtered.filter((p, i, arr) => arr.findIndex(x => Number(x.id) === Number(p.id)) === i);
  }, [people, allStudios, currentLocation, management, ownLocation]);

  const params = useMemo(() => ({
    channel,
    ...(channel === 'studio' ? { locationId } : {}),
    ...(channel === 'dm' && peer ? { peer } : {}),
  }), [channel, locationId, peer]);

  useChatPresence(params, mobileOpen);

  useEffect(() => {
    const generation = ++messageGeneration.current;
    if (params.channel === 'dm' && !params.peer) { setMessages([]); setError(''); return; }
    let active = true;
    setMessages([]);
    setError('');
    const fetchMessages = () => axios.get('/api/chat/messages', { params })
      .then(r => {
        if (!active || generation !== messageGeneration.current) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setMessages(list);
        if (document.visibilityState === 'visible' && (window.innerWidth > 720 || mobileOpen)) {
          const last = Number(list[list.length - 1]?.id) || 0;
          const key = JSON.stringify(params) + ':' + last;
          if (last && lastMarked.current !== key) {
            lastMarked.current = key;
            axios.post('/api/chat/read', { ...params, lastId: last }).then(() => {
              window.dispatchEvent(new Event('aeonas-chat-read'));
            }).catch(() => { lastMarked.current = ''; });
          }
        }
      })
      .catch(e => { if (active && generation === messageGeneration.current) setError('Could not load messages. Please try again.'); });
    fetchMessages();
    const timer = setInterval(fetchMessages, 4000);
    return () => { active = false; ++messageGeneration.current; clearInterval(timer); };
  }, [params, mobileOpen]);

  useEffect(() => {
    const list = end.current?.parentElement;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages[messages.length - 1]?.id, mobileOpen]);

  useChatKeyboardViewport(shell);

  const choose = (nextRoom, nextPeer = null) => {
    setAllStudios(['artist', 'guestspots'].includes(nextRoom));
    setRoom(nextRoom);
    setPeer(nextPeer);
    setShowContacts(false);
    setError('');
    setDraft('');
    setMobileOpen(true);
    if (routeLocation.search) navigate('/chat', { replace: true });
  };
  const send = async e => {
    e.preventDefault();
    if (!peer || !draft.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post('/api/chat/messages', { ...params, body: draft.trim() });
      setDraft('');
      window.dispatchEvent(new Event('aeonas-chat-read'));
      const result = await axios.get('/api/chat/messages', { params });
      setMessages(result.data);
    } catch (err) {
      setError('Message could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const deleteConversation = async () => {
    if (!peer || busy) return;
    if (!window.confirm('Delete this conversation from your account? The other person will keep their messages.')) return;
    const generation = messageGeneration.current;
    setBusy(true); setError('');
    try {
      await axios.delete('/api/chat/conversation', { data: params });
      window.dispatchEvent(new Event('aeonas-chat-read'));
      if (generation !== messageGeneration.current) return;
      ++messageGeneration.current;
      setMessages([]); setDraft(''); setPeer(null); setMobileOpen(false); setShowContacts(false);
      if (routeLocation.search) navigate('/chat', { replace: true });
    } catch (_) {
      if (generation === messageGeneration.current) setError('Conversation could not be deleted. Please try again.');
    } finally { setBusy(false); }
  };

  const selectedPerson = people.find(p => Number(p.id) === Number(peer));
  const title = peer ? selectedPerson?.name || 'Direct message' : 'Choose a conversation';
  const nameById = new Map(people.map(p => [Number(p.id), p]));
  const entries = [
    ...(user?.role==='artist'?ownerContacts:[]).map(p => ({key:'dm:'+p.id,room:'dm',peer:Number(p.id),person:p,name:p.name,subtitle:'Management · Private message'})),
    ...contacts.map(p => ({key:'dm:'+p.id,room:'dm',peer:Number(p.id),person:p,name:p.name,
      subtitle:(p.role==='artist'?'Artist':'Team')+(p.locationId?' · '+(locationNames[p.locationId]||''): '')})),
  ].filter(entry => showContacts || !unread.deleted?.[entry.key] || entry.peer === Number(peer)).sort((a,b)=>(Number(unread.activity?.[b.key])||0)-(Number(unread.activity?.[a.key])||0));


  return <main ref={shell} className={'ae-chat-connect' + (mobileOpen ? ' is-conversation' : '')}
    >
    <aside className="ae-connect-list">
      <header className="ae-connect-heading">
        <button type="button" className="ae-chat-header-back" aria-label="Back to app" title="Back to app" onClick={() => {
          if (window.history.state?.idx > 0) navigate(-1);
          else navigate(user?.role === 'artist' ? '/artist' : '/');
        }}>←</button>
        <span className="ae-connect-mark">✦</span>
        <div><span className="ae-connect-overline">AEONAS</span><h1>Connect</h1></div>
      </header>
      <ChatPushSettings />
      <div className="ae-connect-section ae-chat-list-heading"><span>{showContacts ? 'CONTACTS' : 'CHATS'}</span><button type="button" onClick={() => setShowContacts(value => !value)} aria-label={showContacts ? 'Back to chat list' : 'New chat'} title={showContacts ? 'Back to chat list' : 'New chat'}>{showContacts ? '×' : <FiPlus />}</button></div>
      {entries.length === 0 && <p className="ae-connect-no-contacts">No chats here. Tap + to start a conversation.</p>}
      {entries.map(entry => <button type="button" key={entry.key}
        className={'ae-connect-entry' + (room === entry.room && (entry.room !== 'dm' || Number(peer) === entry.peer) ? ' active' : '')}
        onClick={() => choose(entry.room, entry.peer || null)}>
        {entry.person ? <Avatar artistId={entry.person.artistId} name={entry.person.name}/> : <span className="ae-connect-icon">{entry.icon}</span>}
        <span className="ae-connect-entry-text"><strong>{entry.name}</strong><small>{entry.subtitle}</small></span>
        <ChatBadge count={countFor(entry.key)}/><span className="ae-connect-chevron">›</span>
      </button>)}
    </aside>
    <section className="ae-connect-conversation">
      <header className="ae-connect-conversation-header">
        <button type="button" className="ae-connect-back" onClick={() => setMobileOpen(false)} aria-label="Back to chats">‹</button>
        {peer && <Avatar artistId={selectedPerson?.artistId} name={selectedPerson?.name}/>}
        <div className="ae-connect-title"><span className="ae-connect-overline">AEONAS CONNECT</span><h2>{title}</h2></div>
        {peer && <button type="button" className="ae-chat-delete" onClick={deleteConversation} disabled={busy} aria-label="Delete conversation" title="Delete conversation"><FiTrash2 /></button>}
      </header>
      <div className="ae-connect-messages">
        {messages.length === 0 && <div className="ae-connect-empty"><span>✦</span><h3>{peer ? 'Your conversation starts here.' : 'Choose a conversation'}</h3><p>{peer ? 'Send the first message.' : 'Select someone from your chat list.'}</p></div>}
        {messages.map(m => {
          const own = Number(m.senderId) === Number(user?.id);
          const sender = nameById.get(Number(m.senderId));
          return <div className={'ae-connect-message' + (own ? ' mine' : '')} key={m.id}>
            {!own && <Avatar small artistId={m.senderArtistId || sender?.artistId} name={m.senderName}/>}
            <div className="ae-connect-bubble">
              <div className="ae-connect-message-meta">{m.senderName} · {new Date(m.createdAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
              <div>{m.body}</div>
            </div>
          </div>;
        })}
        <div ref={end}/>
      </div>
      {error && <div className="ae-connect-error">{error}</div>}
      {peer && <form className="ae-connect-compose" onSubmit={send}>
          <input aria-label="Message" placeholder="Write a message…" value={draft} onChange={e => setDraft(e.target.value)} maxLength={4000}/>
          <button type="submit" disabled={busy || !draft.trim()} aria-label="Send message">↗</button>
        </form>}
    </section>
  </main>;
}
